// API呼び出しユーティリティ

import { CalculationInput, ApiResponse, ApiError, CalculationResult } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
console.log('DEBUG: import.meta.env:', import.meta.env);
console.log('DEBUG: VITE_API_URL:', import.meta.env.VITE_API_URL);
console.log('DEBUG: API_BASE_URL:', API_BASE_URL);

/**
 * APIエラークラス
 */
export class ApiRequestError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/**
 * 汎用APIリクエスト関数
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  console.log('Making API request to:', url);
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        const errorData: ApiError = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        // JSON解析に失敗した場合はHTTPステータスメッセージを使用
      }
      
      throw new ApiRequestError(errorMessage, response.status);
    }

    const data: T = await response.json();
    return data;
  } catch (error) {
    if (error instanceof ApiRequestError) {
      throw error;
    }
    
    if (error instanceof TypeError && (error.message.includes('fetch') || error.message.includes('Failed to fetch') || error.message.includes('network'))) {
      throw new ApiRequestError('ネットワークエラーが発生しました。インターネット接続を確認してください。');
    }
    
    throw new ApiRequestError(
      error instanceof Error ? error.message : '予期しないエラーが発生しました。'
    );
  }
}

/**
 * 汎用APIリクエスト関数（レスポンス処理をカスタマイズ可能）
 */
async function apiRequestWithCustomResponse<T>(
  endpoint: string,
  responseHandler: (response: Response) => Promise<T>,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        const errorData: ApiError = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        // JSON解析に失敗した場合はHTTPステータスメッセージを使用
      }
      
      throw new ApiRequestError(errorMessage, response.status);
    }

    return await responseHandler(response);
  } catch (error) {
    if (error instanceof ApiRequestError) {
      throw error;
    }
    
    if (error instanceof TypeError && (error.message.includes('fetch') || error.message.includes('Failed to fetch') || error.message.includes('network'))) {
      throw new ApiRequestError('ネットワークエラーが発生しました。インターネット接続を確認してください。');
    }
    
    throw new ApiRequestError(
      error instanceof Error ? error.message : '予期しないエラーが発生しました。'
    );
  }
}

/**
 * 計算実行API
 */
export async function calculatePieces(input: CalculationInput): Promise<ApiResponse> {
  return apiRequest<ApiResponse>('/calculate', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * CSV生成API
 */
export async function generateCsv(
  data: Array<{
    result: CalculationResult;
    pieceWidth: number;
    pieceHeight: number;
    kerf: number;
  }>
): Promise<Blob> {
  return apiRequestWithCustomResponse<Blob>(
    '/csv',
    (response) => response.blob(),
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

/**
 * SVG生成API
 */
export async function generateSvg(input: {
  materialName: string;
  veneerWidth: number;
  veneerHeight: number;
  pieceWidth: number;
  pieceHeight: number;
  kerf: number;
  horizontalCount: number;
  verticalCount: number;
  totalPieces: number;
}): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/svg`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    
    try {
      const errorData: ApiError = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch {
      // JSON解析に失敗した場合はHTTPステータスメッセージを使用
    }
    
    throw new ApiRequestError(errorMessage, response.status);
  }

  return response.text();
}

/**
 * ヘルスチェックAPI
 */
export async function healthCheck(): Promise<{ status: string; timestamp: string }> {
  return apiRequest<{ status: string; timestamp: string }>('/health', {
    method: 'GET',
  });
}